import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field } from "@/components/common";
import { api, errMsg } from "@/lib/api";

const endOfYear = () => `${new Date().getFullYear()}-12-31`;
const today = () => new Date().toISOString().slice(0, 10);

export function ApproveDialog({ open, onOpenChange, lic, onDone }) {
  const [issue, setIssue] = useState(today());
  const [expiry, setExpiry] = useState(endOfYear());
  const [cats, setCats] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open || !lic) return;
    setIssue(today()); setExpiry(endOfYear()); setCats({});
  }, [open, lic]);

  const submit = async () => {
    setBusy(true);
    try {
      const categories = lic.categories.map((c) => ({ category_id: c.category_id, valid_until: cats[c.category_id] || expiry }));
      await api.post(`/licenses/${lic.id}/approve`, { issue_date: issue, expiry_date: expiry, categories });
      toast.success("Licenza approvata e rilasciata: QR code generato");
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="approve-dialog">
        <DialogHeader><DialogTitle>Approva e rilascia licenza</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">{lic?.athlete_name} · {lic?.discipline_name}</p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Data emissione"><Input type="date" value={issue} onChange={(e) => setIssue(e.target.value)} data-testid="approve-issue-date-input" /></Field>
          <Field label="Data scadenza"><Input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} data-testid="approve-expiry-date-input" /></Field>
        </div>
        <div className="space-y-2">
          <div className="text-sm font-medium text-slate-700">Validità per categoria</div>
          {lic?.categories.map((c) => (
            <div key={c.category_id} className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 p-2 text-sm">
              <span>{c.name}</span>
              <Input type="date" className="w-44" value={cats[c.category_id] || expiry} max={expiry} onChange={(e) => setCats({ ...cats, [c.category_id]: e.target.value })} data-testid={`approve-category-${c.code}-input`} />
            </div>
          ))}
        </div>
        <DialogFooter><Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="approve-submit-btn">Approva e rilascia</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function BulkApproveDialog({ open, onOpenChange, items, onDone }) {
  const [issue, setIssue] = useState(today());
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setIssue(today()); }, [open]);
  const expiry = issue ? `${issue.slice(0, 4)}-12-31` : "";

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/licenses/bulk-approve", { license_ids: items.map((l) => l.id), issue_date: issue });
      if (data.approved.length) toast.success(`${data.approved.length} licenze approvate e rilasciate`);
      if (data.errors.length) toast.error(`${data.errors.length} richieste non approvate: ${data.errors[0].errore}`);
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="bulk-approve-dialog">
        <DialogHeader><DialogTitle>Approva {items.length} richieste</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">Le stesse date verranno applicate a tutte le licenze selezionate. Ogni atleta riceverà l'email di conferma.</p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Data emissione"><Input type="date" value={issue} onChange={(e) => setIssue(e.target.value)} data-testid="bulk-approve-issue-date-input" /></Field>
          <Field label="Data scadenza (31/12)"><Input type="date" value={expiry} disabled readOnly data-testid="bulk-approve-expiry-date-input" /></Field>
        </div>
        <div className="max-h-48 space-y-1 overflow-y-auto rounded-lg border border-slate-200 p-2 text-sm" data-testid="bulk-approve-list">
          {items.map((l) => <div key={l.id} className="flex justify-between gap-2"><span className="font-medium">{l.athlete_name}</span><span className="text-slate-500">{l.discipline_name}</span></div>)}
        </div>
        <DialogFooter><Button onClick={submit} disabled={busy || !issue} className="bg-emerald-600 hover:bg-emerald-700" data-testid="bulk-approve-submit-btn">{busy ? "Approvazione in corso…" : `Approva e rilascia ${items.length}`}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ReasonDialog({ open, onOpenChange, title, description, confirmLabel, danger, onConfirm }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setReason(""); }, [open]);
  const submit = async () => {
    setBusy(true);
    try {
      await onConfirm(reason);
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="reason-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        {description && <p className="text-sm text-slate-600">{description}</p>}
        <Field label="Motivazione (obbligatoria, registrata nel log)">
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reason-input" />
        </Field>
        <DialogFooter>
          <Button onClick={submit} disabled={busy || reason.trim().length < 3} className={danger ? "bg-red-600 hover:bg-red-700" : "bg-[#0F2042] hover:bg-[#1E3A8A]"} data-testid="reason-confirm-btn">{confirmLabel}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
